import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, ClipboardList, MessageSquare, Unlock, CheckCheck, CheckCircle2, XCircle } from "lucide-react";

const ICONS = {
  new_rapportino: ClipboardList,
  edit_request: MessageSquare,
  unlock: Unlock,
  request_approved: CheckCircle2,
  request_rejected: XCircle,
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
};

export const NotificationBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const refresh = () =>
    api.get("/notifications").then((r) => { setItems(r.data.items || []); setUnread(r.data.unread || 0); }).catch(() => {});
  useEffect(() => { refresh(); }, []);
  usePolling(refresh, 15000);

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); setItems((p) => p.map((n) => ({ ...n, read: true }))); setUnread(0); } catch {}
  };

  const onClick = async (n) => {
    setOpen(false);
    if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch {} setUnread((u) => Math.max(0, u - 1)); }
    if (n.url) navigate(n.url);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) refresh(); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" data-testid="notification-bell" className="relative h-9 w-9 shrink-0 rounded-md" title="Notifiche">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span data-testid="notification-dot" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <p className="font-heading text-sm font-bold text-slate-900">Notifiche</p>
          {unread > 0 && (
            <button onClick={markAll} data-testid="notification-mark-all" className="flex items-center gap-1 text-xs font-medium text-[#002FA7] hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> Segna tutte lette
            </button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-slate-400">Nessuna notifica</p>
          ) : (
            items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button key={n.id} onClick={() => onClick(n)} data-testid={`notification-item-${n.id}`}
                  className={`flex w-full items-start gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-blue-50/40"}`}>
                  <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${n.read ? "bg-slate-100 text-slate-500" : "bg-[#002FA7] text-white"}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900">{n.title}</p>
                    <p className="text-xs text-slate-500">{n.body}</p>
                    <p className="mt-0.5 text-[11px] text-slate-400">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-rose-500" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
